// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ProductMultipleSaleQty from "@flip/App/Components/Product/MultipleSaleQty";
import ProductActions, { ProductActionsProps, ProductActionsStyle } from "@flip/App/Components/Product/ProductActions";
import ProductGridContainer, { ProductGridContainerStyle } from "@flip/App/Components/Product/ProductGridTemplate";
import ProductMinimumOrderQty from "@flip/App/Components/Product/ProductMinimumOrderQty";
import ProductPrice, { ProductPriceStyles } from "@flip/Overrides/Components/ProductPrice";
import AngleRight from "@flip/XDL/Icons/AngleRight";
import { getSystemColor } from "@flip/XDL/Utilities";
import { mergeWithCss } from "@flip/XDL/Utilities/mergeWithCss";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductPriceAndActionsProps extends Omit<ProductActionsProps, "extendedStyles"> {
    showAddToList?: boolean;
    showAvailability?: boolean;
    showPrice?: boolean;
    showAddToCartActions?: boolean;
    showCompare?: boolean;
    hideAvailabilityLink?: boolean;
    extendedStyles?: ProductPriceAndActionsStyle;
    onAddToCartClick?: () => void;
    // XNG-Changes:
    showDisabled?: boolean;
    showAddToCartIcon?: boolean;
    showMinimumOrderQty?: boolean;
    showMultipleSaleQty?: boolean;
    isCustomSignInStyle?: boolean;
}
const mapStateToProps = (state: ApplicationState) => ({
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
});

type Props = ProductPriceAndActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;

export interface ProductPriceAndActionsStyle {
    productGridContainer?: ProductGridContainerStyle;
    productActionsStyles?: ProductActionsStyle;
    productPriceStyles?: ProductPriceStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productAddToListStyles?: ProductAddToListLinkStyles;
    productListProductImageStyles?: ProductListProductImageStyles;
    priceAvailabilityLink?: LinkPresentationProps;
}

const productPriceAndActionsStyle: ProductPriceAndActionsStyle = {
    productActionsStyles: {
        addToCartContainer: {
            css: css`
                justify-content: space-between;
            `,
        },
        addToCartButtonWrapper: {
            css: css`
                flex-grow: 1;
            `,
        },
    },
    productListProductImageStyles: {
        wrapper: {
            css: css`
                width: auto;
            `,
        },
        compareCheckbox: {
            css: css`
                margin: 0;
            `,
        },
    },
    priceAvailabilityLink: {
        color: "text.link",
        hoverStyle: {
            color: getSystemColor("common.ui8"),
        },
        icon: {
            iconProps: {
                src: AngleRight,
                size: 5,
                color: "text.link",
            },
            position: "right",
        },
        typographyProps: {
            size: 10,
            weight: 600,
            css: css`
                letter-spacing: 0;
                line-height: 13px;
            `,
        },
        css: css`
            ${IconWrapper} {
                margin-left: 4px;
            }
        `,
    },
};

const ProductPriceAndActions: FC<Props> = ({
    showAddToList = true,
    showAvailability = true,
    showPrice = true,
    extendedStyles,
    showAddToCartActions = true,
    showAddToCartIcon = false,
    showCompare = false,
    hideAvailabilityLink = false,
    productContext,
    canSeePrices,
    signInUrl,
    onAddToCartClick,
    showDisabled,
    showMinimumOrderQty = true,
    showMultipleSaleQty = true,
    isCustomSignInStyle = false,
    ...productActionsProps
}) => {
    const [styles] = useState(() => mergeWithCss(productPriceAndActionsStyle, extendedStyles));

    return (
        <ProductGridContainer extendedStyles={styles.productGridContainer}>
            <ProductGridContainer.PriceAndActions>
                {showPrice && (
                    <ProductGridContainer.Price>
                        <ProductPrice
                            product={productContext}
                            showPack={false}
                            showInvalidPriceMessage={true}
                            isCustomSignInStyle={isCustomSignInStyle}
                            extendedStyles={styles.productPriceStyles}
                        />
                    </ProductGridContainer.Price>
                )}
                {showAvailability && (
                    <ProductGridContainer.Availability>
                        <ProductContextAvailability
                            extendedStyles={styles.productAvailabilityStyles}
                            {...{ hideLink: hideAvailabilityLink }}
                        />
                    </ProductGridContainer.Availability>
                )}
                {showMinimumOrderQty && (
                    <ProductGridContainer.MinimumOrderQty>
                        <ProductMinimumOrderQty product={productContext.product} />
                    </ProductGridContainer.MinimumOrderQty>
                )}
                {showMultipleSaleQty && (
                    <ProductGridContainer.MultipleSaleQty>
                        <ProductMultipleSaleQty product={productContext.product} />
                    </ProductGridContainer.MultipleSaleQty>
                )}
                {showAddToCartActions && (
                    <ProductGridContainer.AddToCartActions>
                        <ProductActions
                            {...productActionsProps}
                            onAddToCartClick={onAddToCartClick}
                            extendedStyles={styles.productActionsStyles}
                        />
                    </ProductGridContainer.AddToCartActions>
                )}
                {(showAddToList || showCompare) && (
                    <ProductGridContainer.AddToList>
                        {showAddToList && <ProductAddToListLink extendedStyles={styles.productAddToListStyles} />}
                        <ProductListProductImage
                            showImage={false}
                            showCompare={showCompare}
                            extendedStyles={styles.productListProductImageStyles}
                        />
                    </ProductGridContainer.AddToList>
                )}
            </ProductGridContainer.PriceAndActions>
        </ProductGridContainer>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductPriceAndActions));
